import React, { useEffect, useState, useRef, Component } from 'react';
import { Send, Search, X, Plus, MoreVertical, ArrowLeft, Paperclip, User, MessageSquare, Info, Bell, Check, Calendar, FileText, Clock, ExternalLink, Download, Trash2, AlertTriangle } from 'lucide-react';
import { useMessages } from '../contexts/MessagesContext';
// Message type definition
type MessageType = 'text' | 'image' | 'file' | 'notification' | 'question' | 'program';
// Message interface
interface Message {
  id: string;
  sender: 'student' | 'teacher' | 'system';
  content: string;
  timestamp: string;
  type?: MessageType;
  attachments?: {
    id: string;
    name: string;
    type: 'image' | 'document' | 'video';
    url: string;
    size?: string;
  }[];
  read?: boolean;
  questionData?: {
    id: number;
    title: string;
    content: string;
    subject: string;
    topic: string;
    options: string[];
    correctOption: number;
    image?: string;
    studentName: string;
    studentAvatar: string;
  };
  programData?: {
    id: number;
    title: string;
    description: string;
    startDate: string;
    endDate: string;
    schedule: {
      day: string;
      subjects: {
        name: string;
        duration: string;
        time: string;
      }[];
    }[];
  };
}
// Conversation interface
interface Conversation {
  id: string;
  type: 'direct' | 'group' | 'support' | 'program' | 'notification' | 'question';
  title: string;
  preview: string;
  status?: 'waiting' | 'processing' | 'completed' | 'pending';
  date: string;
  unread: boolean;
  recipient: {
    id: string;
    name: string;
    role?: string;
    avatar?: string;
  };
  participants?: {
    id: string;
    name: string;
    avatar?: string;
  }[];
  messages: Message[];
}
// Delete Confirmation Modal Component
function DeleteConfirmationModal({
  onClose,
  onConfirm
}: {
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-id="element-2255">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-md" data-id="element-2256">
        <div className="relative bg-black/90 rounded-xl p-6" data-id="element-2257">
          <div className="flex items-center gap-4 mb-4" data-id="element-2258">
            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-2259">
              <AlertTriangle className="w-5 h-5 text-red-400" data-id="element-2260" />
            </div>
            <h3 className="text-xl font-bold" data-id="element-2261">Sohbeti Sil</h3>
          </div>
          <p className="text-gray-300 mb-6" data-id="element-2262">
            Bu sohbeti kalıcı olarak silmek istediğinize emin misiniz? Bu işlem
            geri alınamaz.
          </p>
          <div className="flex justify-end gap-3" data-id="element-2263">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-gray-700 hover:bg-white/5 text-gray-300 rounded-lg transition-colors" data-id="element-2264">
              İptal
            </button>
            <button onClick={onConfirm} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors" data-id="element-2265">
              Sil
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Message Composer Component
function MessageComposer({
  onSendMessage,
  disabled = false
}: {
  onSendMessage: (content: string, attachments?: File[]) => void;
  disabled?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSend = () => {
    if (message.trim() || attachments.length > 0) {
      onSendMessage(message, attachments.length > 0 ? attachments : undefined);
      setMessage('');
      setAttachments([]);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files);
      setAttachments([...attachments, ...fileArray]);
    }
  };
  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };
  return <div className="border-t border-purple-900/30 p-4" data-id="element-2266">
      {attachments.length > 0 && <div className="mb-3 flex flex-wrap gap-2" data-id="element-2267">
          {attachments.map((file, index) => <div key={index} className="flex items-center gap-2 bg-purple-900/20 text-white rounded-lg px-3 py-1.5 text-sm" data-id="element-2268">
              <span className="truncate max-w-[150px]" data-id="element-2269">{file.name}</span>
              <button onClick={() => removeAttachment(index)} className="text-gray-400 hover:text-white" data-id="element-2270">
                <X className="w-4 h-4" data-id="element-2271" />
              </button>
            </div>)}
        </div>}
      <div className="relative" data-id="element-2272">
        <textarea value={message} onChange={e => setMessage(e.target.value)} onKeyDown={handleKeyDown} placeholder={disabled ? 'Bu konuşmaya mesaj gönderemezsiniz' : 'Mesajınızı yazın...'} disabled={disabled} className="w-full bg-black text-white border border-purple-900/30 rounded-lg pl-4 pr-24 py-3 min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2273" />
        <div className="absolute bottom-3 right-3 flex items-center gap-2" data-id="element-2274">
          <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" multiple data-id="element-2275" />
          <button onClick={handleFileSelect} className="p-2 rounded-full bg-black hover:bg-purple-900/20 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-purple-900/50" disabled={disabled} title="Dosya ekle" data-id="element-2276">
            <Paperclip className="w-5 h-5" data-id="element-2277" />
          </button>
          <button onClick={handleSend} disabled={!message.trim() && attachments.length === 0 || disabled} className="p-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2278">
            <Send className="w-5 h-5" data-id="element-2279" />
          </button>
        </div>
      </div>
    </div>;
}
// Conversation List Item Component
function ConversationListItem({
  conversation,
  isActive,
  onClick
}: {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}) {
  // Get icon based on conversation type
  const getIcon = () => {
    switch (conversation.type) {
      case 'direct':
        return User;
      case 'group':
        return MessageSquare;
      case 'support':
        return Info;
      case 'program':
        return Calendar;
      case 'notification':
        return Bell;
      case 'question':
        return FileText;
      default:
        return MessageSquare;
    }
  };
  const Icon = getIcon();
  return <div onClick={onClick} className={`p-3 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/30' : 'hover:bg-purple-900/20 border border-transparent'}`} data-id="element-2280">
      <div className="flex items-center gap-3" data-id="element-2281">
        <div className="relative" data-id="element-2282">
          {conversation.recipient.avatar ? <img src={conversation.recipient.avatar} alt={conversation.recipient.name} className="w-10 h-10 rounded-full object-cover" data-id="element-2283" /> : <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center" data-id="element-2284">
              <Icon className="w-5 h-5" data-id="element-2285" />
            </div>}
          {/* Unread indicator dot */}
          {conversation.unread && <div className="absolute -left-1 top-0 w-3 h-3 bg-purple-500 rounded-full shadow-[0_0_5px_rgba(168,85,247,0.7)]" data-id="element-2286" />}
        </div>
        <div className="flex-1 min-w-0" data-id="element-2287">
          <div className="flex justify-between items-center" data-id="element-2288">
            <h3 className={`text-sm truncate ${conversation.unread ? 'font-bold' : 'font-medium'}`} data-id="element-2289">
              {conversation.title}
            </h3>
            <span className="text-xs text-gray-400 whitespace-nowrap ml-2" data-id="element-2290">
              {conversation.date}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1" data-id="element-2291">
            <p className={`text-xs text-gray-400 truncate ${conversation.unread ? 'font-bold' : 'font-normal'}`} data-id="element-2292">
              {conversation.preview}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// Message Bubble Component
function MessageBubble({
  message
}: {
  message: Message;
}) {
  const isCurrentUser = message.sender === 'student';
  const isSystem = message.sender === 'system';
  // System messages have a special style
  if (isSystem) {
    return <div className="flex justify-center my-4" data-id="element-2293">
        <div className="px-4 py-2 bg-purple-900/20 rounded-lg text-gray-400 text-sm max-w-[80%] border border-purple-900/30" data-id="element-2294">
          {message.content}
        </div>
      </div>;
  }
  // Special rendering for question type
  if (message.type === 'question' && message.questionData) {
    return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-2295">
        <div className="relative max-w-[85%] bg-black border border-purple-800/50 rounded-lg shadow-[0_0_10px_rgba(107,33,168,0.2)] p-4" data-id="element-2296">
          <div className="mb-3 border-b border-purple-900/30 pb-2" data-id="element-2297">
            <h3 className="font-medium text-lg" data-id="element-2298">
              {message.questionData.title}
            </h3>
            <div className="flex items-center gap-2 mt-1" data-id="element-2299">
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-400" data-id="element-2300">
                {message.questionData.subject}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-400" data-id="element-2301">
                {message.questionData.topic}
              </span>
            </div>
          </div>
          <div className="text-sm mb-3" data-id="element-2302">{message.questionData.content}</div>
          {message.questionData.image && <div className="mb-3 rounded-lg overflow-hidden" data-id="element-2303">
              <img src={message.questionData.image} alt="Soru görseli" className="w-full max-h-[200px] object-cover" data-id="element-2304" />
            </div>}
          <div className="space-y-2 mb-3" data-id="element-2305">
            {message.questionData.options.map((option, index) => <div key={index} className={`p-2 rounded-lg text-sm ${index === message.questionData?.correctOption ? 'bg-green-900/20 border border-green-800/50 text-green-400' : 'bg-purple-900/10 border border-purple-900/20'}`} data-id="element-2306">
                <span className="font-medium mr-2" data-id="element-2307">
                  {String.fromCharCode(65 + index)}.
                </span>
                {option}
              </div>)}
          </div>
          <div className="text-xs text-gray-400 mt-2" data-id="element-2308">{message.timestamp}</div>
        </div>
      </div>;
  }
  // Special rendering for program type
  if (message.type === 'program' && message.programData) {
    return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-2309">
        <div className="relative max-w-[85%] bg-black border border-purple-800/50 rounded-lg shadow-[0_0_10px_rgba(107,33,168,0.2)] p-4" data-id="element-2310">
          <div className="mb-3 border-b border-purple-900/30 pb-2" data-id="element-2311">
            <h3 className="font-medium text-lg" data-id="element-2312">{message.programData.title}</h3>
            <p className="text-sm text-gray-400 mt-1" data-id="element-2313">
              {message.programData.description}
            </p>
          </div>
          <div className="flex items-center gap-2 mb-3" data-id="element-2314">
            <Clock className="w-4 h-4 text-purple-400" data-id="element-2315" />
            <span className="text-sm" data-id="element-2316">
              {message.programData.startDate} - {message.programData.endDate}
            </span>
          </div>
          <div className="space-y-3 mb-3" data-id="element-2317">
            {message.programData.schedule.map((day, dayIndex) => <div key={dayIndex} className="bg-purple-900/10 border border-purple-900/30 rounded-lg p-3" data-id="element-2318">
                <h4 className="font-medium text-sm mb-2" data-id="element-2319">{day.day}</h4>
                <div className="space-y-2" data-id="element-2320">
                  {day.subjects.map((subject, subjectIndex) => <div key={subjectIndex} className="flex items-center justify-between text-sm" data-id="element-2321">
                      <span data-id="element-2322">{subject.name}</span>
                      <div className="flex items-center gap-2" data-id="element-2323">
                        <span className="text-gray-400" data-id="element-2324">{subject.time}</span>
                        <span className="bg-purple-900/30 text-purple-400 px-2 py-0.5 rounded-full text-xs" data-id="element-2325">
                          {subject.duration}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
          <div className="flex justify-end gap-2" data-id="element-2326">
            <button className="px-3 py-1.5 text-xs bg-purple-900/20 hover:bg-purple-900/30 rounded-lg flex items-center gap-1 transition-colors" data-id="element-2327">
              <Download className="w-3 h-3" data-id="element-2328" />
              PDF İndir
            </button>
            <button className="px-3 py-1.5 text-xs bg-purple-900/20 hover:bg-purple-900/30 rounded-lg flex items-center gap-1 transition-colors" data-id="element-2329">
              <Calendar className="w-3 h-3" data-id="element-2330" />
              Takvime Ekle
            </button>
          </div>
          <div className="text-xs text-gray-400 mt-2" data-id="element-2331">{message.timestamp}</div>
        </div>
      </div>;
  }
  // Regular message rendering
  return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-2332">
      <div className={`relative max-w-[80%] ${isCurrentUser ? 'bg-gradient-to-r from-purple-600 to-indigo-600 rounded-l-lg rounded-tr-lg shadow-[0_0_10px_rgba(107,33,168,0.3)]' : 'bg-black border border-purple-900/30 rounded-r-lg rounded-tl-lg'} px-4 py-3`} data-id="element-2333">
        {message.content && <p className="text-sm" data-id="element-2334">{message.content}</p>}
        {message.attachments && message.attachments.length > 0 && <div className="mt-2 space-y-2" data-id="element-2335">
            {message.attachments.map(attachment => {
          if (attachment.type === 'image') {
            return <div key={attachment.id} className="rounded-lg overflow-hidden" data-id="element-2336">
                    <img src={attachment.url} alt={attachment.name} className="w-full h-auto max-h-[200px] object-cover" data-id="element-2337" />
                  </div>;
          } else if (attachment.type === 'document') {
            return <div key={attachment.id} className="flex items-center gap-2 p-2 bg-black/30 rounded-lg border border-purple-900/30" data-id="element-2338">
                    <FileText className="w-5 h-5 text-blue-400" data-id="element-2339" />
                    <div className="flex-1 min-w-0" data-id="element-2340">
                      <p className="text-xs font-medium truncate" data-id="element-2341">
                        {attachment.name}
                      </p>
                      {attachment.size && <p className="text-xs text-gray-400" data-id="element-2342">
                          {attachment.size}
                        </p>}
                    </div>
                    <button className="p-1 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors" data-id="element-2343">
                      <Download className="w-4 h-4" data-id="element-2344" />
                    </button>
                  </div>;
          }
          return null;
        })}
          </div>}
        <div className={`text-xs mt-1 ${isCurrentUser ? 'text-white/70' : 'text-gray-400'}`} data-id="element-2345">
          {message.timestamp}
        </div>
      </div>
    </div>;
}
// New Message Modal Component
function NewMessageModal({
  onClose,
  onCreateConversation
}: {
  onClose: () => void;
  onCreateConversation: (recipient: any, message: string) => void;
}) {
  const [selectedRecipient, setSelectedRecipient] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Mock recipients data
  const recipients = [{
    id: 'teacher-1',
    name: 'Ayşe Öğretmen',
    role: 'Matematik Öğretmeni',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&q=80'
  }, {
    id: 'teacher-2',
    name: 'Mehmet Öğretmen',
    role: 'Türkçe Öğretmeni',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&q=80'
  }, {
    id: 'guidance-service',
    name: 'Rehberlik Servisi',
    role: 'Akademik Danışman',
    avatar: null
  }, {
    id: 'support',
    name: 'Teknik Destek',
    role: 'Yardım Masası',
    avatar: null
  }];
  // Filter recipients based on search query
  const filteredRecipients = recipients.filter(recipient => recipient.name.toLowerCase().includes(searchQuery.toLowerCase()) || recipient.role.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleCreateConversation = () => {
    if (selectedRecipient && message.trim()) {
      onCreateConversation(selectedRecipient, message);
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" data-id="element-2346">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 shadow-[0_0_20px_rgba(107,33,168,0.4)] w-full max-w-xl" data-id="element-2347">
        <div className="relative bg-black rounded-xl" data-id="element-2348">
          <div className="p-4 border-b border-purple-900/30 flex justify-between items-center" data-id="element-2349">
            <h2 className="text-xl font-bold" data-id="element-2350">Yeni Mesaj</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-2351">
              <X className="w-6 h-6" data-id="element-2352" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-2353">
            <div className="space-y-2" data-id="element-2354">
              <label className="text-sm font-medium text-gray-300" data-id="element-2355">Alıcı</label>
              <div className="relative" data-id="element-2356">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Öğretmen veya rehber ara..." className="w-full bg-black border border-purple-900/30 text-white rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-1 focus:ring-purple-500/50" data-id="element-2357" />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2358" />
              </div>
            </div>
            <div className="space-y-2 max-h-[200px] overflow-y-auto" data-id="element-2359">
              {filteredRecipients.map(recipient => <div key={recipient.id} onClick={() => setSelectedRecipient(recipient)} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${selectedRecipient?.id === recipient.id ? 'bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/30' : 'hover:bg-purple-900/20 border border-transparent'}`} data-id="element-2360">
                  {recipient.avatar ? <img src={recipient.avatar} alt={recipient.name} className="w-10 h-10 rounded-full object-cover" data-id="element-2361" /> : <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center" data-id="element-2362">
                      <User className="w-5 h-5" data-id="element-2363" />
                    </div>}
                  <div data-id="element-2364">
                    <h3 className="font-medium" data-id="element-2365">{recipient.name}</h3>
                    <p className="text-sm text-gray-400" data-id="element-2366">{recipient.role}</p>
                  </div>
                  {selectedRecipient?.id === recipient.id && <Check className="ml-auto w-5 h-5 text-purple-400" data-id="element-2367" />}
                </div>)}
              {filteredRecipients.length === 0 && <div className="text-center py-4 text-gray-400" data-id="element-2368">
                  Aranan kişi bulunamadı
                </div>}
            </div>
            <div className="space-y-2" data-id="element-2369">
              <label className="text-sm font-medium text-gray-300" data-id="element-2370">Mesaj</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Mesajınızı yazın..." className="w-full bg-black border border-purple-900/30 text-white rounded-lg px-4 py-3 min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-purple-500/50" data-id="element-2371" />
            </div>
          </div>
          <div className="p-4 border-t border-purple-900/30 flex justify-end gap-3" data-id="element-2372">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-purple-900/30 hover:bg-purple-900/10 text-gray-300 rounded-lg transition-colors" data-id="element-2373">
              İptal
            </button>
            <button onClick={handleCreateConversation} disabled={!selectedRecipient || !message.trim()} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2374">
              Gönder
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Main Messages Component
export function Messages() {
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const {
    conversations,
    markConversationAsRead,
    addMessageToConversation,
    addConversation,
    removeConversation
  } = useMessages();
  // Get active conversation
  const activeConversation = activeConversationId ? conversations.find(c => c.id === activeConversationId) : null;
  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conversation => conversation.title.toLowerCase().includes(searchQuery.toLowerCase()) || conversation.recipient.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Scroll to bottom of messages when conversation changes or new message is added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [activeConversation]);
  // Mark conversation as read when opened
  useEffect(() => {
    if (activeConversationId) {
      markConversationAsRead(activeConversationId);
    }
  }, [activeConversationId, markConversationAsRead]);
  // Close options menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSendMessage = (content: string, attachments?: File[]) => {
    if (activeConversationId && (content.trim() || attachments)) {
      const newMessage: Message = {
        id: `m${Date.now()}`,
        sender: 'student',
        content: content.trim(),
        timestamp: `Bugün, ${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}`
      };
      // If there are attachments, add them to the message
      if (attachments && attachments.length > 0) {
        newMessage.attachments = attachments.map(file => ({
          id: `att-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          name: file.name,
          type: file.type.startsWith('image/') ? 'image' : 'document',
          url: URL.createObjectURL(file),
          size: formatFileSize(file.size)
        }));
      }
      addMessageToConversation(activeConversationId, newMessage);
    }
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const handleCreateConversation = (recipient: any, message: string) => {
    // Check if a conversation with this recipient already exists
    const existingConversation = conversations.find(conv => conv.recipient.id === recipient.id);
    if (existingConversation) {
      // If conversation exists, add the message to that conversation
      const newMessage: Message = {
        id: `m${Date.now()}`,
        sender: 'student',
        content: message,
        timestamp: `Bugün, ${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}`
      };
      addMessageToConversation(existingConversation.id, newMessage);
      setActiveConversationId(existingConversation.id);
      return;
    }
    // If no existing conversation, create a new one
    const newConversation: Conversation = {
      id: Date.now().toString(),
      type: 'direct',
      title: recipient.name,
      preview: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
      date: 'Şimdi',
      unread: false,
      recipient,
      messages: [{
        id: 'm1',
        sender: 'student',
        content: message,
        timestamp: `Bugün, ${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}`
      }]
    };
    addConversation(newConversation);
    setActiveConversationId(newConversation.id);
  };
  const handleDeleteConversation = () => {
    if (activeConversationId) {
      removeConversation(activeConversationId);
      setActiveConversationId(null);
      setShowDeleteConfirmation(false);
      setShowOptionsMenu(false);
    }
  };
  // Mobile view state
  const [showConversationList, setShowConversationList] = useState(true);
  // Toggle between conversation list and active conversation on mobile
  const toggleMobileView = () => {
    setShowConversationList(!showConversationList);
  };
  // Determine if we're on mobile based on screen width
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  return <div className="h-[calc(100vh-200px)] min-h-[500px] flex gap-6 bg-black" data-id="element-2375">
      {/* Conversation List - Hide on mobile when viewing a conversation */}
      {(!isMobile || isMobile && showConversationList) && <div className="w-full md:w-80 flex flex-col relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 shadow-[0_0_15px_rgba(107,33,168,0.3)]" data-id="element-2376">
          <div className="flex-1 flex flex-col bg-black rounded-xl overflow-hidden" data-id="element-2377">
            <div className="p-4 border-b border-purple-900/30" data-id="element-2378">
              <div className="flex justify-between items-center mb-4" data-id="element-2379">
                <h2 className="text-xl font-bold" data-id="element-2380">Mesajlar</h2>
                <button onClick={() => setShowNewMessageModal(true)} className="p-2 rounded-full bg-black hover:bg-purple-900/20 text-gray-400 hover:text-white transition-colors border border-purple-900/50" data-id="element-2381">
                  <Plus className="w-5 h-5" data-id="element-2382" />
                </button>
              </div>
              <div className="relative" data-id="element-2383">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Mesaj ara..." className="w-full bg-black border border-purple-900/30 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-1 focus:ring-purple-500/50" data-id="element-2384" />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2385" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white" data-id="element-2386">
                    <X className="w-4 h-4" data-id="element-2387" />
                  </button>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-2388">
              {filteredConversations.length > 0 ? filteredConversations.map(conversation => <ConversationListItem key={conversation.id} conversation={conversation} isActive={conversation.id === activeConversationId} onClick={() => {
            setActiveConversationId(conversation.id);
            if (isMobile) {
              toggleMobileView();
            }
          }} data-id="element-2389" />) : <div className="text-center py-8 text-gray-400" data-id="element-2390">
                  {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz mesaj yok'}
                </div>}
            </div>
            <div className="p-3 border-t border-purple-900/30 bg-black" data-id="element-2391">
              <div className="flex items-center justify-between" data-id="element-2392">
                <div className="flex items-center gap-3" data-id="element-2393">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center" data-id="element-2394">
                    <User className="w-4 h-4" data-id="element-2395" />
                  </div>
                  <div data-id="element-2396">
                    <p className="text-sm font-medium" data-id="element-2397">Ahmet Yılmaz</p>
                    <p className="text-xs text-gray-400" data-id="element-2398">Öğrenci</p>
                  </div>
                </div>
                <button className="p-1.5 rounded-full hover:bg-purple-900/20 text-gray-400 hover:text-white transition-colors" data-id="element-2399">
                  <MoreVertical className="w-4 h-4" data-id="element-2400" />
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Conversation Area - Hide on mobile when viewing the list */}
      {(!isMobile || isMobile && !showConversationList) && <div className="flex-1 flex flex-col relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 shadow-[0_0_15px_rgba(107,33,168,0.3)]" data-id="element-2401">
          <div className="flex-1 flex flex-col bg-black rounded-xl overflow-hidden" data-id="element-2402">
            {activeConversation ? <>
                <div className="p-4 border-b border-purple-900/30" data-id="element-2403">
                  <div className="flex items-center gap-3" data-id="element-2404">
                    {isMobile && <button onClick={toggleMobileView} className="p-1.5 rounded-full hover:bg-purple-900/20 text-gray-400 hover:text-white transition-colors" data-id="element-2405">
                        <ArrowLeft className="w-5 h-5" data-id="element-2406" />
                      </button>}
                    <div className="flex-1" data-id="element-2407">
                      <h2 className="text-xl font-bold" data-id="element-2408">
                        {activeConversation.title}
                      </h2>
                      <p className="text-sm text-gray-400" data-id="element-2409">
                        Alıcı: {activeConversation.recipient.name}
                        {activeConversation.recipient.role && ` (${activeConversation.recipient.role})`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2410">
                      {activeConversation.status && <span className={`px-2 py-1 rounded-full text-xs ${activeConversation.status === 'completed' ? 'bg-green-900/20 text-green-400' : activeConversation.status === 'processing' ? 'bg-yellow-900/20 text-yellow-400' : 'bg-purple-900/20 text-purple-400'}`} data-id="element-2411">
                          {activeConversation.status === 'completed' ? 'Tamamlandı' : activeConversation.status === 'processing' ? 'İşleniyor' : activeConversation.status === 'pending' ? 'Beklemede' : 'Açık'}
                        </span>}
                      <div className="relative" ref={optionsMenuRef} data-id="element-2412">
                        <button className="p-2 rounded-full hover:bg-purple-900/20 text-gray-400 hover:text-white transition-colors" onClick={() => setShowOptionsMenu(!showOptionsMenu)} data-id="element-2413">
                          <MoreVertical className="w-5 h-5" data-id="element-2414" />
                        </button>
                        {showOptionsMenu && <div className="absolute right-0 top-full mt-1 bg-black/95 border border-gray-800 rounded-lg shadow-lg p-1 min-w-[150px] z-50" data-id="element-2415">
                            <button onClick={() => {
                      setShowDeleteConfirmation(true);
                      setShowOptionsMenu(false);
                    }} className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-red-500/20 text-red-400 rounded-lg transition-colors" data-id="element-2416">
                              <Trash2 className="w-4 h-4" data-id="element-2417" />
                              <span data-id="element-2418">Sohbeti Sil</span>
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4" data-id="element-2419">
                  {activeConversation.messages.map(message => <MessageBubble key={message.id} message={message} data-id="element-2420" />)}
                  <div ref={messagesEndRef} data-id="element-2421" />
                </div>
                <MessageComposer onSendMessage={handleSendMessage} disabled={activeConversation.status === 'completed'} data-id="element-2422" />
              </> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-2423">
                <div className="w-16 h-16 rounded-full bg-purple-900/20 flex items-center justify-center mb-4" data-id="element-2424">
                  <MessageSquare className="w-8 h-8 text-purple-400" data-id="element-2425" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-2426">
                  Mesajlarınız burada görünecek
                </h3>
                <p className="text-gray-400 mb-6 max-w-md" data-id="element-2427">
                  Öğretmenleriniz, rehberlik servisi ve diğer öğrencilerle
                  iletişim kurmak için bir konuşma başlatın.
                </p>
                <button onClick={() => setShowNewMessageModal(true)} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-2428">
                  Yeni Mesaj
                </button>
              </div>}
          </div>
        </div>}
      {showNewMessageModal && <NewMessageModal onClose={() => setShowNewMessageModal(false)} onCreateConversation={handleCreateConversation} data-id="element-2429" />}
      {showDeleteConfirmation && <DeleteConfirmationModal onClose={() => setShowDeleteConfirmation(false)} onConfirm={handleDeleteConversation} data-id="element-2430" />}
    </div>;
}
// Context for Messages
export function MessagesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // Implementation would be similar to the useMessages hook
  return <>{children}</>;
}